// Import styles
import './style.scss';

import { recipeTypes as validRecipeTypes } from '@builder-legacy/config/valid-data';

import { html, render } from 'lit';

import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref } from 'lit/directives/ref.js';

/**
 * Creates the recipe type selector
 * Uses Lit
 */
export default class RecipeTypeSelector {
	/**
	 * Create a reference to the dialog
	 *
	 * @type {Ref}
	 */
	$dialog = createRef();

	/**
	 * Invokes the method to render the dialog
	 *
	 * @param {Callback} onReady A callback invoked after the user set a recipe type
	 */
	constructor( onReady ) {
		// Save the callback
		this.onReady = onReady;

		// Send custom event to the document
		document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/show-recipe-type-selector' ) );

		// Render the dialog
		this.render();
	}

	/**
	 * Renders the dialog
	 *
	 * @return {undefined}
	 */
	render() {
		// Render
		render(
			html`
				<uo-dialog
					heading="${ _uo.utility.escHTML( wp.i18n.__( 'Select a recipe type', 'uncanny-automator' ) ) }"
					force-manual-close
					open

					${ ref( this.$dialog ) }
				>
					<div class="uap-recipe-type">
						${
							// Lit's repeat. If you need more information, check
							// https://lit.dev/docs/templates/directives/#repeat
							repeat(
								Object.values( validRecipeTypes ),
								( option ) => option.id,
								( option ) => this._templateType( option )
							)
						}
					</div>

					<uo-alert
						type="info"
						heading="${ _uo.utility.escHTML( wp.i18n.__( 'Recipe type cannot be changed later.', 'uncanny-automator' ) ) }"
						class="uap-spacing-top uap-spacing-top--big"
					></uo-alert>
					
					<uo-button
						slot="secondary-action"
						color="secondary"
						href="${ UncannyAutomator._recipe.miscellaneous.url_trash_recipe.replaceAll( '&amp;', '&' ) }"
					>
						${ _uo.utility.escHTML( wp.i18n._x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>

		    		<uo-button
						slot="primary-action"
						id="uap-recipe-type-confirm"

						@click=${ ( event ) => {
							// Set loading animation on the button
							event.target.loading = true;

							// Save the recipe type
							this.saveRecipeType();
						} }

						?disabled=${ _uo.utility.isEmpty( this.recipeType ) }
					>
						${ _uo.utility.escHTML( wp.i18n._x( 'Confirm', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			document.body
		);

	}

	/**
	 * Generates the template for a type item in a recipe.
	 *
	 * @param {Object} option - The option object for the type item.
	 * @param {String} option.id - The ID of the type item.
	 * @param {String} option.name - The name of the type item.
	 * @param {String} option.description - The description of the type item.
	 *
	 * @returns {TemplateResult} - The HTML template for the type item.
	 */
	_templateType( option ) {
		return html`
			<div
				class="uap-recipe-type-item"
				data-id=${ option.id }
				@click="${ () => this.handleTypeItemClick( option.id ) }"
				?selected=${ option.id === this.recipeType }
			>
				<h3>${ option.name }</h3>
				<p>${ option.description }</p>
			</div>
		`;
	}
	
	/**
	 * Handles the click event for a type item and dispatches a custom event.
	 *
	 * @param {String} typeID - The ID of the type item.
	 */
	handleTypeItemClick( typeID ) {
		// Send custom event to the document
		document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/selected-recipe-type', {
			detail: {
				recipeType: typeID
			},
			bubbles: true,
			composed: true
		} ) );

		// Set the recipe type
		this.recipeType = typeID;
	}

	/**
	 * Saves the recipe type
	 *
	 * @return {undefined}
	 */
	saveRecipeType() {
		UncannyAutomator._core.recipe.setRecipeType(
			this.recipeType
		).then( response => {
			// Check if the call was successful
			if ( ! response.success ) {
				// Check if debugging is enabled
				if ( UncannyAutomatorBackend.debugging.enabled ) {
					// eslint-disable-next-line no-console
					console.error(
						// eslint-disable-next-line max-len
						"Recipe type selector: Couldn't set the recipe type. Open the Network tab and perform the action again."
					);
				}

				return;
			}

			// Save in main object
			UncannyAutomator._recipe.recipe_type = this.recipeType;

			// Close the dialog
			this.$dialog.value.close();

			// Invoke callback defined on the constructor to continue rendering the UI
			this.onReady();
		} );

	}

	/**
	 * The recipe type. When this changes, we have to render the dialog again
	 *
	 * @param {String} recipeType The recipe type
	 * @return {undefined}
	 */
	set recipeType( recipeType = '' ) {
		// Check if it's a valid recipe type
		if ( ! Object.keys( validRecipeTypes ).includes( recipeType ) ) {
			return;
		}

		// Save the value
		this.#_recipeType = recipeType;

		// Render the UI again
		this.render();
	}

	/**
	 * Returns the recipe type
	 *
	 * @return {String} The recipe type
	 */
	get recipeType() {
		return Object.keys( validRecipeTypes ).includes( this.#_recipeType ) ? this.#_recipeType : '';
	}

	#_recipeType;
}
